import type { CSSProperties } from 'react';
import type { RocketReaderPlanet } from '@/data/rocketReaderChallenges';

export type RocketLaunchState = 'idle' | 'charging' | 'launching' | 'landed';

type RocketSceneProps = {
  planet: RocketReaderPlanet;
  launchState: RocketLaunchState;
  completedLaunches: number;
};

export function RocketScene({ planet, launchState, completedLaunches }: RocketSceneProps) {
  const sceneStyle = {
    '--rr-planet': planet.color,
    '--rr-planet-accent': planet.accent,
  } as CSSProperties;

  return (
    <section
      className={`rr-scene rr-scene-${launchState}`}
      style={sceneStyle}
      aria-label={`Rocket scene on ${planet.name}`}
    >
      <div className="rr-star-field" aria-hidden="true" />
      <div className="rr-planet-glow" aria-hidden="true" />
      <div className="rr-planet" aria-hidden="true">
        <div className="rr-planet-ring" />
        <div className="rr-planet-shine" />
      </div>
      <div className="rr-rocket-wrap" aria-hidden="true">
        <svg className="rr-rocket" viewBox="0 0 132 220" role="img">
          <defs>
            <linearGradient id="rrRocketBody" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="55%" stopColor="#e4f3ff" />
              <stop offset="100%" stopColor="#b8d7ff" />
            </linearGradient>
            <linearGradient id="rrFlame" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#fff3a3" />
              <stop offset="55%" stopColor="#ff9f45" />
              <stop offset="100%" stopColor="#ff5f7a" />
            </linearGradient>
          </defs>
          <path
            d="M66 6C39 30 28 68 31 125h70C104 68 93 30 66 6Z"
            fill="url(#rrRocketBody)"
          />
          <path d="M31 117 8 157l32-8 8-32H31Z" fill="#ff7d7d" />
          <path d="M101 117l23 40-32-8-8-32h17Z" fill="#ff7d7d" />
          <circle cx="66" cy="70" r="19" fill="#17356b" />
          <circle cx="61" cy="64" r="6" fill="#89f7ff" opacity="0.9" />
          <path d="M42 125h48l-8 36H50l-8-36Z" fill="#3450a4" />
          <path className="rr-flame" d="M52 160c5 22 14 36 14 52 0-16 9-30 14-52H52Z" fill="url(#rrFlame)" />
          <path d="M50 133h32" stroke="#ffffff" strokeLinecap="round" strokeWidth="7" opacity="0.55" />
        </svg>
      </div>
      <div className="rr-launch-cloud rr-launch-cloud-a" aria-hidden="true" />
      <div className="rr-launch-cloud rr-launch-cloud-b" aria-hidden="true" />
      <div className="rr-scene-label">
        <span>Current planet</span>
        <strong>{planet.name}</strong>
      </div>
      <div className="rr-orbit-chip" aria-label={`${completedLaunches} completed launches`}>
        Launches {completedLaunches}
      </div>
    </section>
  );
}
